/**
 * Centralized logic for calculating previous period dates
 * Used across all reports and insights pages for consistent trend comparisons
 */
import {
	getFiscalQuarterStart,
	getFiscalYearStart,
	getFiscalYearStartMonth,
} from './fiscalYear';

/**
 * Format date as YYYY-MM-DD
 */
const formatDate = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

/**
 * Parse date string as local date (not UTC) to avoid timezone issues
 * '2025-01-01' -> Jan 1, 2025 00:00 local time
 */
const parseLocalDate = (dateString: string): Date => {
	const [year, month, day] = dateString.split('-').map(Number);
	// NaN fallbacks mirror the pre-narrowing behavior: a malformed string
	// produces an Invalid Date exactly as `new Date(undefined, ...)` did.
	return new Date(year ?? NaN, (month ?? NaN) - 1, day ?? NaN);
};

/**
 * Check if a date is the last day of its month
 */
function isLastDayOfMonth(date: Date): boolean {
	const nextDay = new Date(
		date.getFullYear(),
		date.getMonth(),
		date.getDate() + 1,
	);
	return nextDay.getMonth() !== date.getMonth();
}

/**
 * Get the last day of a month
 */
function getLastDayOfMonth(year: number, month: number): Date {
	return new Date(year, month + 1, 0);
}

/**
 * Shift dates back by a number of months
 * Handles month boundary cases (e.g., Oct 31 -> Sep 30, not Oct 1)
 */
function shiftByMonths(
	start: Date,
	end: Date,
	months: number,
): { prevStartDate: string; prevEndDate: string } {
	// Clamp to the target month's last day: May 31 shifted back three months
	// is Feb 28/29, not March 3, and Feb 29 shifted back a year is Feb 28.
	const prevStart = new Date(
		start.getFullYear(),
		start.getMonth() + months,
		Math.min(
			start.getDate(),
			getLastDayOfMonth(start.getFullYear(), start.getMonth() + months).getDate(),
		),
	);

	// Handle end date specially for month boundaries
	let prevEnd: Date;
	if (isLastDayOfMonth(end)) {
		// If original end is last day of month, new end should also be last day of that month
		prevEnd = getLastDayOfMonth(end.getFullYear(), end.getMonth() + months);
	} else {
		prevEnd = new Date(
			end.getFullYear(),
			end.getMonth() + months,
			Math.min(
				end.getDate(),
				getLastDayOfMonth(end.getFullYear(), end.getMonth() + months).getDate(),
			),
		);
	}

	return {
		prevStartDate: formatDate(prevStart),
		prevEndDate: formatDate(prevEnd),
	};
}

/**
 * Shift dates back by a number of years
 */
function shiftByYears(
	start: Date,
	end: Date,
	years: number,
): { prevStartDate: string; prevEndDate: string } {
	const prevStart = new Date(
		start.getFullYear() + years,
		start.getMonth(),
		start.getDate(),
	);
	const prevEnd = new Date(
		end.getFullYear() + years,
		end.getMonth(),
		end.getDate(),
	);

	return {
		prevStartDate: formatDate(prevStart),
		prevEndDate: formatDate(prevEnd),
	};
}

/**
 * Use equal-length rolling period
 * Uses Math.round to handle DST transitions correctly
 */
function useRollingPeriod(
	start: Date,
	end: Date,
): { prevStartDate: string; prevEndDate: string } {
	// Use Math.round instead of Math.ceil to handle DST correctly
	// DST can add/subtract an hour, causing ceil to round up incorrectly
	const daysDiff = Math.round(
		(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
	);

	const prevEnd = new Date(start);
	prevEnd.setDate(prevEnd.getDate() - 1);

	const prevStart = new Date(prevEnd);
	prevStart.setDate(prevStart.getDate() - daysDiff);

	return {
		prevStartDate: formatDate(prevStart),
		prevEndDate: formatDate(prevEnd),
	};
}

/**
 * PD-15754 — `compareTo: 'previousYear'` compares any period with the same
 * dates one year earlier (year-over-year), instead of the default, which
 * compares calendar periods with the one before and rolling periods with the
 * equal-length window just before. `fiscalYearStartMonth` overrides the
 * tenant's configured start month for the fiscal period types.
 */
/** The Monday on or before a date (weeks run Monday to Sunday). */
export function mondayOfWeek(date: Date): Date {
	const daysSinceMonday = (date.getDay() + 6) % 7;
	return new Date(date.getFullYear(), date.getMonth(), date.getDate() - daysSinceMonday);
}

export interface GetPeriodsOptions {
	compareTo?: 'previousPeriod' | 'previousYear';
	fiscalYearStartMonth?: number;
	/**
	 * PD-17428 — the day to resolve the range from, instead of now (local
	 * calendar date; only its year, month and day are read). The schedule
	 * editor uses it to show what each upcoming send will cover.
	 */
	today?: Date;
}

/**
 * Calculate both current and previous period dates based on period type
 * This is the preferred method as it eliminates timezone issues and detection problems
 *
 * @param periodType - Type of period ('today', 'yesterday', 'last7days', 'last30days', 'thisMonth', 'lastMonth', 'thisQuarter', 'lastQuarter', 'thisYear', 'lastYear', 'custom', 'lifetime')
 * @param customStartDate - Start date for custom period (YYYY-MM-DD format, only used when periodType is 'custom')
 * @param customEndDate - End date for custom period (YYYY-MM-DD format, only used when periodType is 'custom')
 * @returns Object with current and previous period dates, or null for lifetime
 *
 * @example
 * // This Year
 * getPeriodsFromType('thisYear')
 * // Returns: { currentStartDate: '2025-01-01', currentEndDate: '2025-11-10', previousStartDate: '2024-01-01', previousEndDate: '2024-11-10' }
 *
 * @example
 * // Custom Range
 * getPeriodsFromType('custom', '2025-03-15', '2025-08-22')
 * // Returns current dates + rolling previous period
 */
export function getPeriodsFromType(
	periodType: string,
	customStartDate?: string,
	customEndDate?: string,
	options: GetPeriodsOptions = {},
): {
	currentStartDate: string;
	currentEndDate: string;
	previousStartDate: string;
	previousEndDate: string;
} | null {
	// Handle lifetime view
	if (periodType === 'lifetime') {
		return null;
	}

	const today =
		options.today != null
			? new Date(options.today.getFullYear(), options.today.getMonth(), options.today.getDate())
			: new Date();
	const fiscalStartMonth =
		options.fiscalYearStartMonth ?? getFiscalYearStartMonth();
	let currentStart: Date;
	let currentEnd: Date;

	// Calculate current period based on type
	switch (periodType) {
		case 'today':
			currentStart = today;
			currentEnd = today;
			break;

		case 'yesterday':
			currentStart = new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate() - 1,
			);
			currentEnd = currentStart;
			break;

		case 'last7days':
			currentEnd = today;
			currentStart = new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate() - 6,
			);
			break;

		case 'last14days':
			currentEnd = today;
			currentStart = new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate() - 13,
			);
			break;

		case 'last30days':
			currentEnd = today;
			currentStart = new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate() - 29,
			);
			break;

		case 'last60days':
			currentEnd = today;
			currentStart = new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate() - 59,
			);
			break;

		case 'last90days':
			currentEnd = today;
			currentStart = new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate() - 89,
			);
			break;

		// PD-16787: "the last 6 months", which reps ask for beside the last 12.
		case 'last180days':
			currentEnd = today;
			currentStart = new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate() - 179,
			);
			break;

		case 'last360days':
			currentEnd = today;
			currentStart = new Date(
				today.getFullYear(),
				today.getMonth(),
				today.getDate() - 359,
			);
			break;

		// PD-17424 — weeks run Monday to Sunday. This week is Monday to today;
		// last week is the whole previous Monday to Sunday.
		case 'thisWeek':
			currentStart = mondayOfWeek(today);
			currentEnd = today;
			break;

		case 'lastWeek': {
			const thisMonday = mondayOfWeek(today);
			currentStart = new Date(
				thisMonday.getFullYear(),
				thisMonday.getMonth(),
				thisMonday.getDate() - 7,
			);
			currentEnd = new Date(
				thisMonday.getFullYear(),
				thisMonday.getMonth(),
				thisMonday.getDate() - 1,
			);
			break;
		}

		case 'thisMonth':
			currentStart = new Date(today.getFullYear(), today.getMonth(), 1);
			currentEnd = today;
			break;

		case 'lastMonth':
			currentStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
			currentEnd = new Date(today.getFullYear(), today.getMonth(), 0);
			break;

		case 'twoMonthsAgo':
			currentStart = new Date(today.getFullYear(), today.getMonth() - 2, 1);
			currentEnd = new Date(today.getFullYear(), today.getMonth() - 1, 0);
			break;

		case 'threeMonthsAgo':
			currentStart = new Date(today.getFullYear(), today.getMonth() - 3, 1);
			currentEnd = new Date(today.getFullYear(), today.getMonth() - 2, 0);
			break;

		case 'fourMonthsAgo':
			currentStart = new Date(today.getFullYear(), today.getMonth() - 4, 1);
			currentEnd = new Date(today.getFullYear(), today.getMonth() - 3, 0);
			break;

		case 'fiveMonthsAgo':
			currentStart = new Date(today.getFullYear(), today.getMonth() - 5, 1);
			currentEnd = new Date(today.getFullYear(), today.getMonth() - 4, 0);
			break;

		case 'thisQuarter': {
			const currentQuarter = Math.floor(today.getMonth() / 3);
			const startMonth = currentQuarter * 3;
			currentStart = new Date(today.getFullYear(), startMonth, 1);
			currentEnd = today;
			break;
		}

		case 'lastQuarter': {
			const currentQuarter = Math.floor(today.getMonth() / 3);
			const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
			const year =
				currentQuarter === 0 ? today.getFullYear() - 1 : today.getFullYear();
			const startMonth = lastQuarter * 3;
			const endMonth = startMonth + 3;
			currentStart = new Date(year, startMonth, 1);
			currentEnd = new Date(year, endMonth, 0);
			break;
		}

		case 'thisYear':
			currentStart = new Date(today.getFullYear(), 0, 1);
			currentEnd = today;
			break;

		case 'lastYear':
			currentStart = new Date(today.getFullYear() - 1, 0, 1);
			currentEnd = new Date(today.getFullYear() - 1, 11, 31);
			break;

		// PD-15754 — fiscal periods follow the tenant's fiscal year start month.
		case 'thisFiscalYear':
			currentStart = getFiscalYearStart(today, fiscalStartMonth);
			currentEnd = today;
			break;

		case 'lastFiscalYear': {
			const thisFiscalStart = getFiscalYearStart(today, fiscalStartMonth);
			currentStart = new Date(
				thisFiscalStart.getFullYear() - 1,
				thisFiscalStart.getMonth(),
				1,
			);
			currentEnd = new Date(
				thisFiscalStart.getFullYear(),
				thisFiscalStart.getMonth(),
				0,
			);
			break;
		}

		case 'thisFiscalQuarter':
			currentStart = getFiscalQuarterStart(today, fiscalStartMonth);
			currentEnd = today;
			break;

		case 'lastFiscalQuarter': {
			const thisQuarterStart = getFiscalQuarterStart(today, fiscalStartMonth);
			currentStart = new Date(
				thisQuarterStart.getFullYear(),
				thisQuarterStart.getMonth() - 3,
				1,
			);
			currentEnd = new Date(
				thisQuarterStart.getFullYear(),
				thisQuarterStart.getMonth(),
				0,
			);
			break;
		}

		case 'custom':
			if (!customStartDate || !customEndDate) {
				throw new Error(
					'Custom period requires customStartDate and customEndDate',
				);
			}

			currentStart = parseLocalDate(customStartDate);
			currentEnd = parseLocalDate(customEndDate);
			break;

		default:
			throw new Error(`Unknown period type: ${periodType}`);
	}

	// Calculate previous period based on period type
	let previousStart: Date;
	let previousEnd: Date;

	// For calendar periods (month, quarter, year), shift by the same unit
	if (options.compareTo === 'previousYear') {
		const shifted = shiftByMonths(currentStart, currentEnd, -12);
		previousStart = parseLocalDate(shifted.prevStartDate);
		previousEnd = parseLocalDate(shifted.prevEndDate);
	} else if (
		periodType === 'thisFiscalYear' ||
		periodType === 'lastFiscalYear'
	) {
		const shifted = shiftByMonths(currentStart, currentEnd, -12);
		previousStart = parseLocalDate(shifted.prevStartDate);
		previousEnd = parseLocalDate(shifted.prevEndDate);
	} else if (
		periodType === 'thisFiscalQuarter' ||
		periodType === 'lastFiscalQuarter'
	) {
		const shifted = shiftByMonths(currentStart, currentEnd, -3);
		previousStart = parseLocalDate(shifted.prevStartDate);
		previousEnd = parseLocalDate(shifted.prevEndDate);
	} else if (
		periodType === 'thisMonth' ||
		periodType === 'lastMonth' ||
		periodType === 'twoMonthsAgo' ||
		periodType === 'threeMonthsAgo' ||
		periodType === 'fourMonthsAgo' ||
		periodType === 'fiveMonthsAgo'
	) {
		const shifted = shiftByMonths(currentStart, currentEnd, -1);
		previousStart = parseLocalDate(shifted.prevStartDate);
		previousEnd = parseLocalDate(shifted.prevEndDate);
	} else if (periodType === 'thisWeek' || periodType === 'lastWeek') {
		// The same days of the week before (Monday to the same weekday), not
		// the days just ahead of Monday that the rolling rule would pick.
		previousStart = new Date(
			currentStart.getFullYear(),
			currentStart.getMonth(),
			currentStart.getDate() - 7,
		);
		previousEnd = new Date(
			currentEnd.getFullYear(),
			currentEnd.getMonth(),
			currentEnd.getDate() - 7,
		);
	} else if (periodType === 'thisQuarter' || periodType === 'lastQuarter') {
		const shifted = shiftByMonths(currentStart, currentEnd, -3);
		previousStart = parseLocalDate(shifted.prevStartDate);
		previousEnd = parseLocalDate(shifted.prevEndDate);
	} else if (periodType === 'thisYear' || periodType === 'lastYear') {
		const shifted = shiftByYears(currentStart, currentEnd, -1);
		previousStart = parseLocalDate(shifted.prevStartDate);
		previousEnd = parseLocalDate(shifted.prevEndDate);
	} else {
		// For rolling periods (today, yesterday, last X days, custom), use rolling logic
		const rolled = useRollingPeriod(currentStart, currentEnd);
		previousStart = parseLocalDate(rolled.prevStartDate);
		previousEnd = parseLocalDate(rolled.prevEndDate);
	}

	return {
		currentStartDate: formatDate(currentStart),
		currentEndDate: formatDate(currentEnd),
		previousStartDate: formatDate(previousStart),
		previousEndDate: formatDate(previousEnd),
	};
}
